import { useEffect, useState } from 'react';

import type { TrustpilotRequestStatus } from '@/lib/postLiveSolicitation';
import { trustpilotService, type TrustpilotReviewRequest } from '../api/trustpilotService';

interface TrustpilotReviewRequestState {
  status: TrustpilotRequestStatus;
  request: TrustpilotReviewRequest | null;
}

const NO_REQUEST: TrustpilotReviewRequestState = { status: 'none', request: null };

/** Le back clôture la session 14 s après le clic (tâche différée) et ne crée la demande qu'ensuite :
 *  on la redemande une fois passé ce délai avant de conclure qu'il n'y en a pas. */
export const REVIEW_REQUEST_SETTLE_DELAY_MS = 20_000;

/** Interroge la demande d'avis avant d'ouvrir quoi que ce soit : c'est elle qui décide si l'avis
 *  interne est sauté. L'appel n'a lieu qu'au retour d'une clôture de live. */
export function useTrustpilotReviewRequest(
  isReturningFromLive: boolean,
): TrustpilotReviewRequestState {
  const [state, setState] = useState<TrustpilotReviewRequestState>(
    isReturningFromLive ? { status: 'loading', request: null } : NO_REQUEST,
  );

  useEffect(() => {
    if (!isReturningFromLive) return;

    let isStillMounted = true;
    let retryTimeoutId: ReturnType<typeof setTimeout> | undefined;
    let hasRetried = false;

    const load = async () => {
      try {
        const request = await trustpilotService.getReviewRequest();
        if (!isStillMounted) return;
        if (request !== null) {
          setState({ status: 'active', request });
        } else if (!hasRetried) {
          hasRetried = true;
          retryTimeoutId = setTimeout(() => void load(), REVIEW_REQUEST_SETTLE_DELAY_MS);
        } else {
          setState(NO_REQUEST);
        }
      } catch {
        // Une sollicitation manquée ne doit jamais retenir l'avis interne.
        if (isStillMounted) setState(NO_REQUEST);
      }
    };

    void load();

    return () => {
      isStillMounted = false;
      clearTimeout(retryTimeoutId);
    };
  }, [isReturningFromLive]);

  return state;
}
